import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChefHat, Clock } from 'lucide-react';
import { useGameLoopContext } from '../app/GameProviders';
import { CONSTANTS } from '../data';
import type { ServingDish } from '../types';

const ServingDishItem = React.memo(({ dish }: { dish: ServingDish }) => {
  const material = (CONSTANTS.MATERIALS as any)[dish.dishId];
  if (!material) return null;

  return (
    <motion.div
      key={dish.id}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="bg-slate-700/50 rounded-lg p-3 flex items-center justify-between"
    >
      <div className="flex items-center gap-2">
        <span className="text-xl">{material.icon}</span>
        <span className="text-sm font-medium text-cyan-200">{material.name}</span>
      </div>
      <div className="flex items-center gap-1 text-xs text-slate-400">
        <Clock className="w-3 h-3" />
        <span>{Math.ceil((CONSTANTS.SERVING_AREA.WAITER_PICKUP_TIME - (Date.now() - dish.timestamp)) / 1000)}s</span>
      </div>
    </motion.div>
  );
});

export function ServingArea() {
  const { servingQueue, servingAreaRef } = useGameLoopContext();

  return (
    <div
      ref={servingAreaRef}
      className="fixed right-4 bottom-4 z-40"
    >
      <div className="bg-slate-800/90 backdrop-blur rounded-xl p-4 w-64 border border-slate-700 shadow-lg">
        <div className="flex items-center gap-2 mb-3">
          <ChefHat className="w-5 h-5 text-amber-400" />
          <span className="font-semibold text-white">Serving Area</span>
        </div>
        
        <div className="text-xs text-slate-300 mb-3">
          Drop completed dishes here for the waiter to serve to customers.
        </div>

        <div className="space-y-2 max-h-48 overflow-y-auto">
          <AnimatePresence>
            {servingQueue.map((dish) => (
              <ServingDishItem key={dish.id} dish={dish} />
            ))}
          </AnimatePresence>
          
          {servingQueue.length === 0 && (
            <div className="text-center py-6 text-slate-400 text-sm">
              <div className="w-16 h-16 mx-auto mb-2 rounded-full bg-slate-700/30 flex items-center justify-center">
                <ChefHat className="w-8 h-8 text-slate-500" />
              </div>
              Drop dishes here to serve
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
